import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Search, Send, CheckCircle2, Clock, XCircle, Ban, CalendarClock } from "lucide-react";
import { ScheduleDialog, SocietyAppointmentsTab } from "./ExpertScheduling";

const STATUS_TONE = {
  SUBMITTED: "bg-amber-100 text-amber-800",
  ACCEPTED: "bg-emerald-100 text-emerald-800",
  DECLINED: "bg-rose-100 text-rose-800",
  CANCELLED: "bg-muted text-muted-foreground",
};
const STATUS_ICON = { SUBMITTED: Clock, ACCEPTED: CheckCircle2, DECLINED: XCircle, CANCELLED: Ban };

export default function Experts() {
  const [tab, setTab] = useState("discover");
  const [experts, setExperts] = useState(null);
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [q, setQ] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [detail, setDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sched, setSched] = useState(null);

  const loadExperts = (service_id = "", query = "") =>
    api.get("/expert-discovery/experts", { params: { service_id, q: query } }).then((r) => setExperts(r.data)).catch(() => setExperts([]));
  const loadRequests = () => api.get("/expert-discovery/requests").then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/expert-discovery/services").then((r) => setServices(r.data)).catch(() => {});
    loadExperts();
    loadRequests();
  }, []);

  const search = () => loadExperts(serviceFilter, q);

  const openRequest = (expert) => {
    setSelected(expert);
    setForm({ service_id: expert.services?.[0]?.service_id || "", subject: "", description: "", preferred_mode: "", preferred_date_time_note: "", idempotency_key: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` });
  };

  const submit = async () => {
    if (!form.service_id) { toast.error("Please choose a service"); return; }
    if (!form.subject?.trim()) { toast.error("Subject is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/expert-discovery/requests", { expert_id: selected.expert_id, ...form });
      toast.success("Consultation request sent to the expert");
      setSelected(null);
      setForm({});
      loadRequests();
      setTab("requests");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (requestId) => {
    try { setDetail((await api.get(`/expert-discovery/requests/${requestId}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load request"); }
  };

  const cancel = async (requestId) => {
    try {
      await api.post(`/expert-discovery/requests/${requestId}/cancel`);
      toast.success("Request cancelled");
      setDetail(null);
      loadRequests();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  const activeCount = useMemo(() => requests.filter((r) => r.status === "SUBMITTED").length, [requests]);

  if (!experts) return <Loading />;
  return (
    <div>
      <PageHeader title="Expert Consultations" subtitle="Discover verified professionals and send a consultation request. Our team coordinates the rest." />

      <div className="flex gap-2 border-b mb-6">
        <button data-testid="experts-tab-discover" onClick={() => setTab("discover")} className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${tab === "discover" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>Discover Experts</button>
        <button data-testid="experts-tab-requests" onClick={() => setTab("requests")} className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${tab === "requests" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>My Consultation Requests{activeCount > 0 && <span className="ml-1.5 text-xs bg-amber-100 text-amber-800 rounded-full px-1.5">{activeCount}</span>}</button>
        <button data-testid="experts-tab-appointments" onClick={() => setTab("appointments")} className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${tab === "appointments" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>Appointments</button>
      </div>

      {tab === "appointments" && <SocietyAppointmentsTab />}

      {tab === "discover" && (
        <div>
          <div className="flex flex-wrap gap-2 mb-5">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="expert-search-input" className="pl-9" placeholder="Search name, title or service" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
            </div>
            <select data-testid="expert-service-filter" className="h-10 border rounded-md px-3 text-sm min-w-[180px]" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
              <option value="">All services</option>
              {services.map((s) => <option key={s.service_id} value={s.service_id}>{`${s.category_name} · ${s.name}`}</option>)}
            </select>
            <Button data-testid="expert-search-btn" onClick={search} className="rounded-full gap-2"><Search className="w-4 h-4" />Search</Button>
          </div>

          {experts.length === 0 ? <EmptyState icon={GraduationCap} title="No experts match" subtitle="Try a different service or search term." /> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {experts.map((e) => (
                <div key={e.expert_id} className="bg-card border rounded-xl p-5 flex flex-col" data-testid={`expert-card-${e.expert_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center"><GraduationCap className="w-5 h-5" /></div>
                    <div>
                      <div className="font-display font-bold flex items-center gap-1.5">{e.name}
                        {e.verified && <span className="text-[10px] bg-emerald-100 text-emerald-800 rounded-full px-1.5 py-0.5 flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" />Verified</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{e.professional_title || "Professional"}</div>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-3 space-y-1 flex-1">
                    {e.bio && <p className="line-clamp-2 text-foreground/80">{e.bio}</p>}
                    {e.experience && <div>Experience: {e.experience} yrs</div>}
                    {(e.city || e.state) && <div>{[e.city, e.state].filter(Boolean).join(", ")}</div>}
                    {e.languages && <div>Languages: {e.languages}</div>}
                    <div className="flex flex-wrap gap-1 pt-1">{(e.services || []).map((s) => <span key={s.service_id} className="text-[11px] bg-muted rounded-full px-2 py-0.5">{s.name}</span>)}</div>
                  </div>
                  <Button onClick={() => openRequest(e)} className="rounded-full w-full mt-4 gap-2" data-testid={`expert-request-${e.expert_id}`}><Send className="w-4 h-4" />Request Consultation</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "requests" && (
        requests.length === 0 ? <EmptyState icon={GraduationCap} title="No consultation requests yet" subtitle="Discover an expert and send your first request." /> : (
          <div className="space-y-2" data-testid="consultation-requests-list">
            {requests.map((r) => {
              const Icon = STATUS_ICON[r.status] || Clock;
              return (
                <button key={r.request_id} onClick={() => openDetail(r.request_id)} className="w-full text-left bg-card border rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap hover:border-primary/40 transition-colors" data-testid={`request-row-${r.request_id}`}>
                  <div>
                    <div className="font-medium">{r.expert_snapshot?.name} — {r.service_snapshot?.name}</div>
                    <div className="text-xs text-muted-foreground">{r.subject}</div>
                  </div>
                  <span className={`text-xs rounded-full px-2.5 py-1 flex items-center gap-1 ${STATUS_TONE[r.status]}`}><Icon className="w-3.5 h-3.5" />{r.status}</span>
                </button>
              );
            })}
          </div>
        )
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="consultation-dialog">
          <DialogHeader><DialogTitle className="font-display">Request Consultation — {selected?.name}</DialogTitle><DialogDescription className="sr-only">Send a consultation request to this expert</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1.5 block text-sm">Service *</Label>
              <select data-testid="consultation-service" className="h-10 w-full border rounded-md px-3 text-sm" value={form.service_id || ""} onChange={(e) => setForm({ ...form, service_id: e.target.value })}>
                <option value="">Select a service</option>
                {(selected?.services || []).map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
              </select>
            </div>
            <div><Label className="mb-1.5 block text-sm">Subject *</Label><Input data-testid="consultation-subject" value={form.subject || ""} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
            <div><Label className="mb-1.5 block text-sm">Details</Label><Textarea data-testid="consultation-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe what you need help with" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-sm">Preferred mode</Label>
                <select data-testid="consultation-mode" className="h-10 w-full border rounded-md px-3 text-sm" value={form.preferred_mode || ""} onChange={(e) => setForm({ ...form, preferred_mode: e.target.value })}>
                  <option value="">No preference</option>
                  {["phone", "audio", "video", "chat", "in_person"].map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
                </select>
              </div>
              <div><Label className="mb-1.5 block text-sm">Preferred timing</Label><Input data-testid="consultation-timing" value={form.preferred_date_time_note || ""} onChange={(e) => setForm({ ...form, preferred_date_time_note: e.target.value })} placeholder="e.g. weekday mornings" /></div>
            </div>
            <div className="bg-muted rounded-lg p-3 text-xs text-muted-foreground">This sends a consultation request to the expert. No payment is collected here.</div>
          </div>
          <DialogFooter><Button onClick={submit} disabled={submitting} className="rounded-full" data-testid="consultation-submit">{submitting ? "Sending…" : "Send Request"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="request-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">Consultation Request</DialogTitle><DialogDescription className="sr-only">Consultation request details and timeline</DialogDescription></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-base">{detail.subject}</span>
                <span className={`text-xs rounded-full px-2.5 py-1 ${STATUS_TONE[detail.status]}`}>{detail.status}</span>
              </div>
              <Row label="Expert" value={detail.expert_snapshot?.name} />
              <Row label="Service" value={detail.service_snapshot?.name} />
              {detail.description && <Row label="Details" value={detail.description} />}
              {detail.preferred_mode && <Row label="Preferred mode" value={detail.preferred_mode.replace("_", " ")} />}
              {detail.preferred_date_time_note && <Row label="Preferred timing" value={detail.preferred_date_time_note} />}
              {detail.status === "DECLINED" && detail.decline_reason && <Row label="Expert note" value={detail.decline_reason} />}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Timeline</p>
                <ul className="space-y-1">{(detail.timeline || []).map((t, i) => <li key={i} className="text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{t.status} · {new Date(t.at).toLocaleString()}</li>)}</ul>
              </div>
              {detail.status === "SUBMITTED" && <Button variant="outline" className="rounded-full text-destructive w-full" onClick={() => cancel(detail.request_id)} data-testid="request-cancel-btn"><Ban className="w-4 h-4 mr-1" />Cancel Request</Button>}
              {detail.status === "ACCEPTED" && <Button className="rounded-full w-full" onClick={() => setSched({ requestId: detail.request_id })} data-testid="request-schedule-btn"><CalendarClock className="w-4 h-4 mr-1" />Schedule Consultation</Button>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ScheduleDialog open={!!sched} requestId={sched?.requestId} appointmentId={sched?.appointmentId} onClose={() => setSched(null)} onDone={() => { setSched(null); setDetail(null); setTab("appointments"); }} />
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex gap-2"><span className="text-muted-foreground min-w-[110px]">{label}</span><span className="font-medium">{value}</span></div>;
}
